import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ShipWheel, Users, MoveHorizontal, MessageCircle, Sparkles, X, ZoomIn, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { useLanguage } from '../context/LanguageContext';
import { PREMIUM_BOATS } from '../data/premiumBoats';
export const PremiumFleet = () => {
  const {
    t
  } = useLanguage();
  const [lightbox, setLightbox] = useState<{
    image: string;
    name: string;
  } | null>(null);
  const [galleryIndex, setGalleryIndex] = useState<Record<string, number>>({});
  const advanceGallery = (boatId: string, gallery: string[], direction: 1 | -1) => {
    setGalleryIndex(prev => {
      const current = prev[boatId] ?? 0;
      const next = (current + direction + gallery.length) % gallery.length;
      return {
        ...prev,
        [boatId]: next
      };
    });
  };
  const handleDragEnd = (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo, boatId: string, gallery: string[]) => {
    const swipeThreshold = 50;
    if (info.offset.x > swipeThreshold) {
      advanceGallery(boatId, gallery, -1);
    } else if (info.offset.x < -swipeThreshold) {
      advanceGallery(boatId, gallery, 1);
    }
  };
  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox]);
  const handleWhatsApp = (boatName: string) => {
    const message = t('wa.premium').replace('[BARCO]', boatName);
    window.open(getWhatsAppUrl(message), '_blank');
  };
  return <section id="premium" className="py-16 sm:py-20 md:py-24 bg-slate-50" data-id="element-466" data-mp="1imzue8hev940">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-467" data-mp="1ptu9g59barvb">
        {/* HEADER */}
        <div className="text-center mb-12 sm:mb-16" data-id="element-468" data-mp="1urvomf3ma71i">
          <div className="inline-flex items-center justify-center mb-4" data-id="element-469" data-mp="1zqgtirttdewa">
            <div className="bg-turquoise/10 text-turquoise border border-turquoise/20 font-bold uppercase text-xs sm:text-sm px-4 py-2 rounded-full tracking-wider" data-id="element-470" data-mp="1zo44jupn249q">
              {t('premium.badge')}
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-navy mb-4 uppercase tracking-tight whitespace-pre-line" data-id="element-471" data-mp="2dh2dkjv07uvr">
            {t('premium.title')}
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 font-medium mb-4" data-id="element-472" data-mp="2nfrkxip9xwxr">
            {t('premium.subtitle')}
          </p>
          <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-473" data-mp="o37418i94ial">
            {t('premium.description')}
          </p>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10" data-id="element-474" data-mp="lhza7q7yq0s9">
          {PREMIUM_BOATS.map((boat, idx) => {
          const isFeatured = boat.id === 'puravida';
          const specialBadge = isFeatured ? t('premium.boat.puravida.badge') : undefined;
          const note = boat.id === 'lagoon' ? t('premium.boat.lagoon.note') : undefined;
          return <motion.article key={boat.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: idx * 0.1,
            duration: 0.6
          }} className={`relative rounded-[12px] overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 flex flex-col group bg-navy ${isFeatured ? 'ring-2 ring-[#C9A84C]/60 shadow-[0_0_40px_rgba(201,168,76,0.25)]' : ''}`} data-id="element-475" data-mp="14hd8ulgmzzp3" data-mpi={idx}>
                {(() => {
              const currentIdx = galleryIndex[boat.id] ?? 0;
              const currentImage = boat.gallery?.[currentIdx] ?? boat.image;
              const hasGallery = !!boat.gallery && boat.gallery.length > 1;
              return <div className="relative w-full aspect-[4/3] sm:aspect-[3/2] overflow-hidden" data-id="element-476" data-mp="1lq4bzidy3jrt">
                      <button type="button" onClick={() => setLightbox({
                  image: currentImage,
                  name: boat.name
                })} aria-label={t('premium.aria.zoom')} className="absolute inset-0 w-full h-full overflow-hidden cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-turquoise/60 z-0" data-id="element-477" data-mp="1qno1xfk09kzl">
                        <AnimatePresence mode="wait" initial={false} data-id="element-478" data-mp="2ek14fxgdwjse">
                          <motion.img key={currentImage} src={currentImage} alt={boat.name} loading={idx < 2 ? 'eager' : 'lazy'} fetchPriority={idx < 2 ? 'high' : 'auto'} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.35
                    }} drag={hasGallery ? 'x' : false} dragDirectionLock style={{
                      touchAction: hasGallery ? 'pan-y' : undefined
                    }} dragConstraints={{
                      left: 0,
                      right: 0
                    }} dragElastic={0.2} onDragEnd={hasGallery ? (e, info) => handleDragEnd(e, info, boat.id, boat.gallery!) : undefined} className="absolute inset-0 w-full h-full object-cover" data-id="element-479" data-mp="2elvg0l5rmzjt" />
                        </AnimatePresence>
                      </button>

                      {/* Gradients */}
                      <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/60 to-transparent pointer-events-none z-0" data-id="element-480" data-mp="22yos4rlv8b1l" />
                      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/50 to-transparent pointer-events-none z-0" data-id="element-481" data-mp="1y6x3imm9sfe3" />

                      {/* CAROUSEL ARROWS */}
                      {hasGallery && <>
                          <button type="button" onClick={e => {
                    e.stopPropagation();
                    advanceGallery(boat.id, boat.gallery!, -1);
                  }} aria-label={t('premium.aria.prev')} className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-white/15 hover:bg-white/30 backdrop-blur-md text-white rounded-full transition-all hover:scale-110 shadow-lg" data-id="element-482" data-mp="1k7nv494b2pko">
                            <ChevronLeft className="w-5 h-5" data-id="element-483" data-mp="2igq3twwgxt5e" />
                          </button>
                          <button type="button" onClick={e => {
                    e.stopPropagation();
                    advanceGallery(boat.id, boat.gallery!, 1);
                  }} aria-label={t('premium.aria.next')} className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center bg-white/15 hover:bg-white/30 backdrop-blur-md text-white rounded-full transition-all hover:scale-110 shadow-lg" data-id="element-484" data-mp="23rtyi7lsqu8h">
                            <ChevronRight className="w-5 h-5" data-id="element-485" data-mp="3f8jvd04by9pf" />
                          </button>

                          {/* Dots */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5 pointer-events-none" data-id="element-486" data-mp="18jrpa6tjs3jj">
                            {boat.gallery!.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === currentIdx ? 'bg-white w-5' : 'bg-white/50 w-1.5'}`} data-id="element-487" data-mp="13iu5xqwsx3gg" data-mpi={i} />)}
                          </div>
                        </>}

                      {/* Zoom hint */}
                      <button type="button" onClick={() => setLightbox({
                  image: currentImage,
                  name: boat.name
                })} aria-label={t('premium.aria.zoom')} className="absolute top-4 right-4 sm:hidden w-11 h-11 flex items-center justify-center bg-white/20 backdrop-blur-md text-white rounded-full z-20 hover:bg-white/30 transition-colors" data-id="element-488" data-mp="1nb0jyucb4f3j">
                        <ZoomIn className="w-5 h-5" data-id="element-489" data-mp="24rlxytbthou6" />
                      </button>

                      {/* CONTENT OVERLAY */}
                      <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between z-10 pointer-events-none" data-id="element-490" data-mp="1vp49s9clqm4j">
                        <div className="flex justify-between items-start gap-3" data-id="element-491" data-mp="2sfdany19s3vz">
                          <div className="flex flex-wrap gap-1.5" data-id="element-492" data-mp="2nfiilr5rd89c">
                            <div className="bg-[#0A1F44]/90 backdrop-blur-sm text-white rounded-md px-1.5 py-1 flex flex-col items-center justify-center min-w-[42px] sm:min-w-[48px] shadow-lg border border-white/10" data-id="element-493" data-mp="2ni3l8idvrs85">
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase text-white/80 leading-none" data-id="element-494" data-mp="3b5lb28dl4g8h">
                                {t('premium.captain')}
                              </span>
                              <ShipWheel className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-turquoise my-0.5" data-id="element-495" data-mp="sws14mcobx98" />
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase leading-none" data-id="element-496" data-mp="xutkz9hxc0lr">
                                {t('premium.captainLabel')}
                              </span>
                            </div>

                            <div className="bg-[#0A1F44]/90 backdrop-blur-sm text-white rounded-md px-1.5 py-1 flex flex-col items-center justify-center min-w-[42px] sm:min-w-[48px] shadow-lg border border-white/10" data-id="element-497" data-mp="16xkx7bimj0eg">
                              <Users className="w-3 h-3 text-turquoise" data-id="element-498" data-mp="1gs338nbij81f" />
                              <span className="text-sm sm:text-base font-black leading-none my-0.5" data-id="element-499" data-mp="1lm7fnm3js89w">
                                {boat.specs.capacity}
                              </span>
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase leading-none" data-id="element-500" data-mp="1umx4u6yaz0en">
                                {t('premium.max')}
                              </span>
                            </div>

                            <div className="bg-[#0A1F44]/90 backdrop-blur-sm text-white rounded-md px-1.5 py-1 flex flex-col items-center justify-center min-w-[42px] sm:min-w-[48px] shadow-lg border border-white/10" data-id="element-501" data-mp="3vlqzq10jzhq9">
                              <MoveHorizontal className="w-3 h-3 text-turquoise" data-id="element-502" data-mp="4hiskzztgu8x" />
                              <span className="text-sm sm:text-base font-black leading-none my-0.5" data-id="element-503" data-mp="4fftzrkllssl">
                                {boat.specs.length}
                              </span>
                              <span className="text-[8px] sm:text-[9px] font-bold uppercase leading-none" data-id="element-504" data-mp="e72e6dewz27h">
                                {t('premium.meters')}
                              </span>
                            </div>

                            {specialBadge && <div className="bg-[#C9A84C]/90 backdrop-blur-sm text-white rounded-md px-2 py-1 flex flex-col items-center justify-center shadow-lg border border-white/20" data-id="element-505" data-mp="2tz9gyr6azsny">
                                <Sparkles className="w-3 h-3 mb-0.5" data-id="element-506" data-mp="14idx6y1n9gxa" />
                                <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-wider text-center leading-tight whitespace-pre-line" data-id="element-507" data-mp="19gyvgrxc5qj3">
                                  {specialBadge.split(' ').join('\n')}
                                </span>
                              </div>}
                          </div>

                          <h3 className="text-lg sm:text-2xl md:text-3xl font-black text-white uppercase text-right drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] tracking-wide shrink-0" data-id="element-508" data-mp="lsxuc42hlcdp">
                            {boat.name}
                          </h3>
                        </div>

                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 sm:gap-6 mt-auto" data-id="element-509" data-mp="gsa079iyoff9">
                          <div className="flex-1 max-w-md order-2 sm:order-1 w-full sm:w-auto pointer-events-auto" data-id="element-510" data-mp="2b5sv0bllxjwv">
                            <button onClick={() => handleWhatsApp(boat.name)} className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1ebd5a] text-white px-3 py-2 rounded-full font-black uppercase tracking-wider flex items-center justify-center gap-1.5 text-[10px] sm:text-xs transition-transform hover:scale-105 shadow-lg" data-id="element-511" data-mp="b41q8a3bb5xz">
                              <MessageCircle className="w-3.5 h-3.5" data-id="element-512" data-mp="3oabje6tn3z0c" />
                              {t('premium.reserve')}
                            </button>
                          </div>

                          <div className="flex flex-col items-start sm:items-end gap-1.5 order-1 sm:order-2 w-full sm:w-auto" data-id="element-513" data-mp="3fh8bsf5oegdm">
                            <div className="flex flex-wrap sm:flex-nowrap justify-start sm:justify-end gap-2 w-full" data-id="element-514" data-mp="3ajyb23cu6iah">
                              {boat.prices.map((price, i) => <div key={i} className="bg-[#E63946] text-white rounded-lg px-2 py-1 flex flex-col items-center shadow-lg border border-white/10 min-w-[58px] sm:min-w-[64px]" data-id="element-515" data-mp="30k7joajkojem" data-mpi={i}>
                                  <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider opacity-90 leading-none" data-id="element-516" data-mp="30egnnfhjuwtp">
                                    {price.duration}
                                  </span>
                                  <span className="text-xs sm:text-sm font-black leading-none mt-0.5" data-id="element-517" data-mp="27rm54o4am7iv">
                                    {price.amount}
                                  </span>
                                </div>)}
                            </div>
                            {note && <span className="text-[11px] sm:text-xs font-medium text-white/80 drop-shadow-md mt-1 ml-1 sm:ml-0" data-id="element-518" data-mp="1ozkw4sxt10vq">
                                {note}
                              </span>}
                            <span className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wide text-white/85 drop-shadow-md mt-0.5 ml-1 sm:ml-0" data-id="element-519" data-mp="1lwwwz9uh2idy">
                              <Clock className="w-3 h-3 flex-shrink-0" data-id="element-520" data-mp="1vob1xx570aeh" />
                              {t('boat.durationInfo')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>;
            })()}
              </motion.article>;
        })}
        </div>
      </div>

      {/* LIGHTBOX */}
      <AnimatePresence data-id="element-521" data-mp="1cybhjnkarjhe">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={() => setLightbox(null)} className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out" role="dialog" aria-modal="true" aria-label={`Foto ampliada de ${lightbox.name}`} data-id="element-522" data-mp="1cuxf6fbmva9y">
            <button type="button" onClick={e => {
          e.stopPropagation();
          setLightbox(null);
        }} aria-label={t('premium.aria.close')} className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full p-3 transition-colors" data-id="element-523" data-mp="2hy9srahr6kfu">
              <X className="w-6 h-6" data-id="element-524" data-mp="30syhqnp40k9d" />
            </button>

            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10" data-id="element-525" data-mp="34vl5aceqzj0r">
              <div className="bg-white/10 backdrop-blur-md text-white font-black uppercase tracking-wider px-4 py-2 rounded-full text-sm sm:text-base" data-id="element-526" data-mp="3slp4m7kwkwly">
                {lightbox.name}
              </div>
            </div>

            <motion.img initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} src={lightbox.image} alt={lightbox.name} decoding="async" onClick={e => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default" data-id="element-527" data-mp="38nckakw8za2q" />
          </motion.div>}
      </AnimatePresence>
    </section>;
};